import { useCallback, useEffect, useRef } from 'react';

/**
 * Keeps react-hook-form in step with browser autofill.
 *
 * RHF registers inputs uncontrolled and reads their values from `input` events.
 * Browsers fill fields straight into the DOM without firing those events, so the
 * form believes a field is empty while the user is plainly looking at a filled
 * one — and `required` then blocks the submit with a message that contradicts
 * the screen.
 *
 * `autoComplete="off"` on the form is the first line of defence, but Chrome
 * ignores it in several cases (a field called `name`, `email` or `address` in
 * particular), so this reconciles the DOM back into the form state.
 *
 * Attach `formRef` to the <form>. Pass no `fields` and every named control in
 * the form is synced, which is what most forms want; pass a list to restrict it.
 *
 * @param {object} options
 * @param {(name: string, value: string, opts?: object) => void} options.setValue  RHF setValue
 * @param {string[]} [options.fields]  field names to sync; omit for all of them
 */
export function useAutofillSync({ setValue, fields } = {}) {
  const formRef = useRef(null);

  /** The live DOM value for a field, falling back to what RHF thinks it holds. */
  const readDom = useCallback((name, fallback = '') => {
    const input = formRef.current?.elements?.namedItem(name);
    // A radio group returns a RadioNodeList, which also exposes `.value`.
    return input?.value || fallback;
  }, []);

  const fieldKey = fields ? fields.join(',') : '*';

  useEffect(() => {
    if (!setValue) return undefined;

    const sync = () => {
      const form = formRef.current;
      if (!form) return;

      // Without an explicit list, every named control in the form is synced —
      // one less thing to keep in step when a field is added.
      const names = fields || [
        ...new Set(
          [...form.elements]
            .map((el) => el.name)
            .filter((n) => n && !n.startsWith('__'))
        ),
      ];

      names.forEach((name) => {
        const input = form.elements.namedItem(name);
        if (!input) return;
        // Checkboxes and file inputs carry no meaningful `.value` to copy.
        if (input.type === 'checkbox' || input.type === 'file') return;
        if (input.value) {
          setValue(name, input.value, { shouldValidate: false, shouldDirty: true });
        }
      });
    };

    // Chrome applies autofill asynchronously and sometimes more than once, so a
    // single check on mount is not enough.
    sync();
    const timers = [80, 250, 600].map((ms) => setTimeout(sync, ms));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, fieldKey]);

  return { formRef, readDom };
}

export default useAutofillSync;
